import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { ScaleContext, type ScaleChoice } from './scale-context.js'
import { firstChoice, remember, type DisplayScale } from './useDisplayScale.js'

/**
 * Holds the chosen scale for everything below it.
 *
 * One copy, so choosing a size on the profile page reaches the layout, the key
 * legend and anything else that asked. Each of those used to keep its own —
 * fine while the only way to set the scale was a query string read once at
 * startup, and wrong the moment there is a switch somebody can press.
 *
 * Its own file so the hooks beside it are not exported from a component's
 * module, which costs the whole file its fast refresh.
 */
export function ScaleProvider({ children }: { children: ReactNode }) {
  const [scale, setScale] = useState<DisplayScale>(firstChoice)

  // Written down whenever it changes, so the choice survives a reload. In the
  // browser rather than against the account, deliberately: it is a fact about
  // the screen this is being read on, not about the person. The same account
  // on the television wants the big scale and on the tablet does not.
  useEffect(() => {
    remember(scale)
  }, [scale])

  const choose = useCallback((next: DisplayScale) => {
    setScale(next)
  }, [])

  const value = useMemo<ScaleChoice>(() => ({ scale, choose }), [scale, choose])

  return <ScaleContext.Provider value={value}>{children}</ScaleContext.Provider>
}
